/* ============================================================
   BOOST CUSTOMISATION 2026-06-21 - PDP image watermarks
   ------------------------------------------------------------
   Drives snippets/boost-pdp-image-watermarks.liquid.

   Responsibilities:
     1. Position the watermark layer over the *active* main product
        image's CANVAS inside the Xtra/Swiper gallery (.l4pr). The marks
        hug the canvas frame (the <picture>, which fills the fixed
        aspect-ratio slide box), bottom-left (brand) and bottom-right
        (Boost), so they stay in the canvas corners even when the image
        is letterboxed (e.g. a landscape image in a portrait canvas).
        [BOOST CUSTOMISATION 2026-07-05: previously hugged the <img>,
        which Xtra shrinks to the contained image, so the marks floated
        up to the middle on landscape images.]
     2. Keep the layer anchored across slide changes, thumbnail clicks,
        resizes and late image loads. Never duplicates onto thumbnails.
     3. Hide both marks when the active slide is a video or 3D model.
     4. Brand mark click navigates to the brand collection and is stopped
        from reaching the gallery's lightbox trigger. The Boost mark is
        decorative (pointer-events:none in CSS) so the zoom click works
        through it.
     5. Fancybox lightbox: hide the page marks while open, and inject the
        brand logo to the left of the Fancybox thumbnail carousel,
        linking to the brand collection.

   Pure vanilla JS. No dependencies. Loaded only on product templates.
   ============================================================ */
(function () {
  'use strict';

  function init() {
    var layer = document.querySelector('[data-boost-watermark-layer]');
    if (!layer) return; // excluded product (VIP / Decoration / sample) or no PDP

    var container = layer.closest('.l4pr-container') || layer.parentElement;
    if (!container) return;

    var gallery = container.querySelector('.l4pr');
    if (!gallery) {
      // No gallery found; nothing to anchor to.
      return;
    }

    /* --------------------------------------------------------
       Geometry: overlay the active main image.
       -------------------------------------------------------- */

    // The active main slide. Swiper marks it .swiper-slide-active. Guard
    // against matching a thumbnail (those use .swiper-pagination-bullet-active,
    // not .swiper-slide-active, but exclude the pagination subtree to be safe).
    function getActiveSlide() {
      var actives = gallery.querySelectorAll('.swiper-slide-active');
      for (var i = 0; i < actives.length; i++) {
        if (!actives[i].closest('.swiper-pagination-bullets')) return actives[i];
      }
      // Fallbacks for non-Swiper (single image) or pre-init states.
      return (
        gallery.querySelector('.swiper-slide') ||
        gallery.querySelector(':scope > li') ||
        gallery.querySelector('li')
      );
    }

    // A slide is a photo unless it carries a video play icon or 3D model marker.
    function isPhotoSlide(slide) {
      if (!slide) return false;
      if (slide.querySelector('.icon-play, .model-3d, .icon-cube')) return false;
      return true;
    }

    // BOOST CUSTOMISATION 2026-07-05 - anchor to the CANVAS, not the image.
    // The framed canvas to hug: the <picture> fills the fixed aspect-ratio
    // slide box (.l4pr li sets the ratio via padding-top; .l4pr li picture is
    // width:100%/height:100% and object-fit:contain), so its rect is the
    // CANVAS box, letterbox included, not the shrunk visible-image box.
    // Anchoring here keeps the marks in the canvas bottom corners for
    // landscape images instead of floating up to the bottom of the shorter
    // visible image. Fallbacks cover any non-<picture> markup.
    function getCanvasEl(slide) {
      return (
        slide.querySelector('picture') ||
        slide.querySelector('a') ||
        slide
      );
    }

    // The visible image element, used ONLY to gate visibility until the image
    // has painted (its rect is 0 until load). Geometry comes from the canvas
    // above, never from this element.
    function getImageEl(slide) {
      return (
        slide.querySelector('picture img') ||
        slide.querySelector('img')
      );
    }

    // Vertically align the Boost mark's centre with the brand mark's centre.
    // The brand logo height varies by vendor, so this is measured at runtime.
    // The Boost img is transform-scaled about its bottom-right, so its visual
    // bottom sits at the span's bottom offset; centre = bottom + visualH/2.
    // Solve for the bottom offset that puts that centre on the brand centre.
    function alignBoostVertically() {
      var brand = layer.querySelector('.boost-pdp-watermark--brand');
      var boost = layer.querySelector('.boost-pdp-watermark--boost');
      if (!boost) return;
      if (!brand) {
        boost.style.bottom = ''; // no brand mark: fall back to the CSS bottom
        return;
      }
      var lr = layer.getBoundingClientRect();
      // Measure the brand IMG, not its <a>: the img is transform-scaled, and
      // getBoundingClientRect on the img reflects that scale, so this is the
      // brand mark's true visual box.
      var brandImgEl = brand.querySelector('img');
      var br = brandImgEl ? brandImgEl.getBoundingClientRect() : brand.getBoundingClientRect();
      if (!br.height) return;
      var brandCentreFromBottom = lr.bottom - (br.top + br.height / 2);

      var bimg = boost.querySelector('img');
      // getBoundingClientRect reflects the transform, so this is the scaled
      // (visual) height.
      var bh = bimg ? bimg.getBoundingClientRect().height : boost.getBoundingClientRect().height;
      if (!bh) return;

      boost.style.bottom = (brandCentreFromBottom - bh / 2) + 'px';
    }

    function update() {
      var slide = getActiveSlide();
      if (!slide || !isPhotoSlide(slide)) {
        layer.classList.remove('is-visible');
        return;
      }
      // BOOST CUSTOMISATION 2026-07-05 - measure the canvas frame; gate on the image.
      var canvas = getCanvasEl(slide);
      if (!canvas) {
        layer.classList.remove('is-visible');
        return;
      }
      var cr = canvas.getBoundingClientRect();
      if (cr.width < 8 || cr.height < 8) {
        // Canvas not laid out yet.
        layer.classList.remove('is-visible');
        return;
      }
      // Hold the marks hidden until the visible image has actually painted
      // (its rect is 0 until load), so they never flash over an empty canvas.
      var img = getImageEl(slide);
      if (img) {
        var ir = img.getBoundingClientRect();
        if (ir.width < 8 || ir.height < 8) {
          layer.classList.remove('is-visible');
          return;
        }
      }

      // Position relative to the layer's offsetParent. Works whether the
      // gallery container is sticky (desktop), static (mobile) or anything
      // else, including when the offsetParent is the document element.
      var op = layer.offsetParent || document.documentElement;
      var opr = op.getBoundingClientRect();
      var cs = window.getComputedStyle(op);
      var bt = parseFloat(cs.borderTopWidth) || 0;
      var bl = parseFloat(cs.borderLeftWidth) || 0;

      // Size the layer to the CANVAS box (not the visible image), so the marks
      // pin to the canvas bottom-left / bottom-right corners in every case.
      layer.style.top = cr.top - opr.top - bt + 'px';
      layer.style.left = cr.left - opr.left - bl + 'px';
      layer.style.width = cr.width + 'px';
      layer.style.height = cr.height + 'px';
      layer.classList.add('is-visible');

      // Level the Boost mark with the vendor logo's mid-point.
      alignBoostVertically();
    }

    // Coalesce rapid calls into one rAF tick.
    var rafPending = false;
    function scheduleUpdate() {
      if (rafPending) return;
      rafPending = true;
      window.requestAnimationFrame(function () {
        rafPending = false;
        update();
      });
    }

    /* --------------------------------------------------------
       Brand mark: intercept the click so it navigates to the brand
       collection rather than opening the lightbox.
       -------------------------------------------------------- */
    var brandMark = layer.querySelector('[data-boost-watermark-brand]');
    if (brandMark) {
      brandMark.addEventListener('click', function (e) {
        // Let the anchor's own navigation proceed; just stop the event
        // from reaching any delegated lightbox handler.
        e.stopPropagation();
      });
      // Some galleries bind on pointer/mousedown; stop those too.
      brandMark.addEventListener('mousedown', function (e) {
        e.stopPropagation();
      });
      brandMark.addEventListener('touchstart', function (e) {
        e.stopPropagation();
      }, { passive: true });
    }

    /* --------------------------------------------------------
       Observers / listeners to keep the overlay aligned.
       -------------------------------------------------------- */

    // Slide changes flip the .swiper-slide-active class; child/attribute
    // mutations in the gallery also affect layout.
    var moGallery = new MutationObserver(scheduleUpdate);
    moGallery.observe(gallery, {
      attributes: true,
      attributeFilter: ['class', 'style'],
      childList: true,
      subtree: true
    });

    // Resize of the gallery (responsive reflow, sticky transitions).
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(scheduleUpdate);
      ro.observe(gallery);
      ro.observe(container);
    }
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    window.addEventListener('orientationchange', scheduleUpdate, { passive: true });
    window.addEventListener('scroll', scheduleUpdate, { passive: true });

    // Re-measure as images load (rect is 0 until then).
    gallery.addEventListener('load', scheduleUpdate, true);

    // The watermark images live outside .l4pr, so listen for their loads too;
    // the brand logo's height is needed before the Boost mark can be levelled.
    var wmImgs = layer.querySelectorAll('img');
    for (var wi = 0; wi < wmImgs.length; wi++) {
      if (!wmImgs[wi].complete) {
        wmImgs[wi].addEventListener('load', scheduleUpdate);
      }
    }

    // Swiper sizes slides asynchronously; settle with a few timed passes.
    [0, 100, 300, 600, 1200, 2000].forEach(function (t) {
      window.setTimeout(scheduleUpdate, t);
    });

    /* --------------------------------------------------------
       Fancybox v5 lightbox handling: hide the on-image page marks while the
       lightbox is open, and restore them on close. (The lightbox brand logo
       was removed.)
       -------------------------------------------------------- */
    function onLightboxOpen() {
      layer.classList.add('is-suppressed');
    }

    function onLightboxClose() {
      layer.classList.remove('is-suppressed');
      scheduleUpdate();
    }

    function nodeIsFbContainer(n) {
      return n.nodeType === 1 &&
        (n.classList && n.classList.contains('fancybox__container'));
    }

    var moBody = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        var j;
        for (j = 0; j < m.addedNodes.length; j++) {
          var an = m.addedNodes[j];
          if (nodeIsFbContainer(an)) {
            onLightboxOpen(an);
          } else if (an.nodeType === 1 && an.querySelector) {
            var inner = an.querySelector('.fancybox__container');
            if (inner) onLightboxOpen(inner);
          }
        }
        for (j = 0; j < m.removedNodes.length; j++) {
          var rn = m.removedNodes[j];
          if (nodeIsFbContainer(rn) ||
              (rn.nodeType === 1 && rn.querySelector && rn.querySelector('.fancybox__container'))) {
            onLightboxClose();
          }
        }
      }
    });
    moBody.observe(document.body, { childList: true });

    // First pass.
    scheduleUpdate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
